import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import MemoryGridPuzzle from '../components/puzzles/MemoryGridPuzzle';
import PatternPuzzle from '../components/puzzles/PatternPuzzle';
import LateralPuzzle from '../components/puzzles/LateralPuzzle';
import WordAssocPuzzle from '../components/puzzles/WordAssocPuzzle';
import VocabPuzzle from '../components/puzzles/VocabPuzzle';
import './Puzzles.css';

// ── Types ────────────────────────────────────────────────────────────────────

interface PuzzleSet {
  nanoid: string;
  puzzle_date: string;
  memory_grid: any;
  pattern_sequence: any;
  lateral_thinking: any;
  word_association: any;
  vocab_word: any;
}

type TabKey = 'welcome' | 'wordassoc' | 'vocab' | 'memory' | 'patterns' | 'riddle';

const TABS: { key: TabKey; label: string; emoji: string }[] = [
  { key: 'welcome',   label: 'Home',      emoji: '🏠' },
  { key: 'wordassoc', label: 'Word',      emoji: '🔤' },
  { key: 'vocab',     label: 'Vocab',     emoji: '📖' },
  { key: 'memory',    label: 'Memory',    emoji: '🧠' },
  { key: 'patterns',  label: 'Sequences', emoji: '🔢' },
  { key: 'riddle',    label: 'Riddle',    emoji: '💡' },
];

function formatDate(dateStr: string): string {
  // dateStr may arrive as "2026-05-29" or "2026-05-29T00:00:00.000Z" from the DB driver
  const [year, month, day] = String(dateStr).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function PuzzlesPage() {
  const { nanoid } = useParams<{ nanoid: string }>();
  const [puzzles, setPuzzles] = useState<PuzzleSet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('welcome');

  useEffect(() => {
    if (!nanoid) return;
    fetch(`/api/puzzles/${nanoid}`)
      .then(r => r.json())
      .then(data => {
        if (!data.success) throw new Error(data.error || 'Not found');
        setPuzzles(data.data);
      })
      .catch(err => setError(err.message || 'Failed to load puzzles'))
      .finally(() => setLoading(false));
  }, [nanoid]);

  if (loading) {
    return (
      <div className="pz-page">
        <PublicHeader />
        <div className="pz-center">
          <div className="pz-spinner" />
        </div>
      </div>
    );
  }

  if (error || !puzzles) {
    return (
      <div className="pz-page">
        <PublicHeader />
        <div className="pz-center">
          <p className="pz-not-found">These puzzles weren't found. Check that your link is correct.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pz-page">
      <PublicHeader />
      <div className="pz-root">
        <div className="pz-container">
          <div className="pz-header">
            <p className="pz-date">{formatDate(puzzles.puzzle_date)}</p>
            <h1 className="pz-title">Daily Brain Teasers</h1>
            <p className="pz-subtitle">Solve them all — or just the ones you like.</p>
          </div>

          <div className="pz-tabs">
            {TABS.map(tab => (
              <button
                key={tab.key}
                className={`pz-tab ${activeTab === tab.key ? 'pz-tab-active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span className="pz-tab-emoji">{tab.emoji}</span>
                <span className="pz-tab-label">{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="pz-tab-content">
            {activeTab === 'welcome' && (
              <div className="pz-panel pz-welcome-panel">
                <p className="pz-welcome-date">{formatDate(puzzles.puzzle_date)}</p>
                <h2 className="pz-welcome-heading">Your daily brain teasers are ready!</h2>
                <p className="pz-welcome-sub">Five puzzles, fresh every day. Pick one to get started.</p>
                <div className="pz-welcome-cards">
                  {([
                    { key: 'wordassoc', emoji: '🔤', name: 'Word Association', desc: 'See the word — submit every association you can think of in 2 minutes.' },
                    { key: 'vocab',     emoji: '📖', name: 'Word of the Day',   desc: 'A rare word almost nobody knows. Spend hints, then pick the meaning.' },
                    { key: 'memory',    emoji: '🧠', name: 'Memory Grid',       desc: 'Memorise a grid of emojis, then recall which ones were hidden.' },
                    { key: 'patterns',  emoji: '🔢', name: 'Sequences',         desc: 'Find the next number in three patterns of increasing difficulty.' },
                    { key: 'riddle',    emoji: '💡', name: 'Riddle',            desc: 'A lateral thinking puzzle — creative answers welcome.' },
                  ] as const).map(p => (
                    <button
                      key={p.key}
                      className="pz-welcome-card"
                      onClick={() => setActiveTab(p.key)}
                    >
                      <span className="pz-welcome-card-emoji">{p.emoji}</span>
                      <span className="pz-welcome-card-name">{p.name}</span>
                      <span className="pz-welcome-card-desc">{p.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'wordassoc' && (
              <div className="pz-panel">
                <div className="pz-panel-header">
                  <h2 className="pz-panel-title">Word Association</h2>
                  <p className="pz-panel-hint">
                    You see the word — think fast! Submit as many associated words as you can before time runs out.
                  </p>
                </div>
                {puzzles.word_association ? (
                  <WordAssocPuzzle nanoid={puzzles.nanoid} wordAssoc={puzzles.word_association} />
                ) : (
                  <p className="pz-muted">Word association puzzle not available for this day.</p>
                )}
              </div>
            )}

            {activeTab === 'vocab' && (
              <div className="pz-panel">
                <div className="pz-panel-header">
                  <h2 className="pz-panel-title">Word of the Day</h2>
                  <p className="pz-panel-hint">
                    A genuinely obscure word. You have a budget of 3 hints — spend as few as you can, then pick the meaning.
                  </p>
                </div>
                {puzzles.vocab_word ? (
                  <VocabPuzzle nanoid={puzzles.nanoid} vocab={puzzles.vocab_word} />
                ) : (
                  <p className="pz-muted">Word of the Day not available for this day.</p>
                )}
              </div>
            )}

            {activeTab === 'memory' && (
              <div className="pz-panel">
                <div className="pz-panel-header">
                  <h2 className="pz-panel-title">Memory Grid</h2>
                  <p className="pz-panel-hint">
                    Memorise the emoji grid, then recall which ones were hidden.
                  </p>
                </div>
                {puzzles.memory_grid ? (
                  <MemoryGridPuzzle nanoid={puzzles.nanoid} memoryGrid={puzzles.memory_grid} />
                ) : (
                  <p className="pz-muted">Memory grid not available for this day.</p>
                )}
              </div>
            )}

            {activeTab === 'patterns' && (
              <div className="pz-panel">
                <div className="pz-panel-header">
                  <h2 className="pz-panel-title">Number Sequences</h2>
                  <p className="pz-panel-hint">
                    Find the next number. 3 attempts per sequence.
                  </p>
                </div>
                <PatternPuzzle
                  nanoid={puzzles.nanoid}
                  sequences={puzzles.pattern_sequence?.sequences ?? []}
                />
              </div>
            )}

            {activeTab === 'riddle' && (
              <div className="pz-panel">
                <div className="pz-panel-header">
                  <h2 className="pz-panel-title">Lateral Thinking</h2>
                  <p className="pz-panel-hint">
                    There may be more than one valid answer — think creatively.
                  </p>
                </div>
                <LateralPuzzle nanoid={puzzles.nanoid} lateral={puzzles.lateral_thinking} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
